import { useEffect, useRef, useState, useCallback } from "react";
import { MessagesSquare, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { auth } from "../../lib/firebase";

const timeOf = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

/* Trip Group Chat — private thread for everyone on a shared trip (any role, incl. view-only).
   Polls for new messages every 5s while the tab is visible. */
export default function TripChat({ tripId, isOwner }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const lastRef = useRef("");
  const myUid = auth.currentUser?.uid;

  const scrollDown = () => endRef.current?.scrollIntoView({ block: "nearest" });

  const load = useCallback(async (initial = false) => {
    try {
      const r = await api.get(`/trips/${tripId}/chat`, { params: initial ? {} : { after: lastRef.current } });
      const items = r.data.items || [];
      if (initial) {
        setMsgs(items);
        if (items.length) lastRef.current = items[items.length - 1].created_at;
        setTimeout(scrollDown, 50);
      } else if (items.length) {
        lastRef.current = items[items.length - 1].created_at;
        setMsgs((cur) => [...(cur || []), ...items].slice(-200));
        setTimeout(scrollDown, 50);
      }
    } catch { if (initial) setMsgs([]); }
  }, [tripId]);

  useEffect(() => {
    load(true);
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(false); }, 5000);
    return () => clearInterval(iv);
  }, [load]);

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/trips/${tripId}/chat`, { text: t });
      setText("");
      lastRef.current = r.data.created_at;
      setMsgs((cur) => [...(cur || []), r.data].slice(-200));
      setTimeout(scrollDown, 50);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send message"); }
    setBusy(false);
  };

  const remove = async (mid) => {
    try {
      await api.delete(`/trips/${tripId}/chat/${mid}`);
      setMsgs((cur) => (cur || []).filter((m) => m.id !== mid));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete message"); }
  };

  return (
    <div className="border border-zinc-300 bg-white flex flex-col" data-testid="trip-chat">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-zinc-200 bg-zinc-50">
        <MessagesSquare className="w-4 h-4 text-indigo-600" />
        <span className="font-700 text-sm">Trip Chat</span>
        <span className="text-[11px] text-muted-foreground ml-auto">everyone on this trip</span>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 max-h-72 lg:max-h-[420px] min-h-[120px]" data-testid="trip-chat-messages">
        {msgs === null && <div className="text-xs text-muted-foreground py-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading chat…</div>}
        {msgs?.length === 0 && <p className="text-xs text-muted-foreground py-4 text-center">No messages yet — say hi and start planning together.</p>}
        {(msgs || []).map((m) => {
          const mine = m.user_id === myUid;
          return (
            <div key={m.id} className={`group flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`trip-chat-msg-${m.id}`}>
              <div className={`max-w-[85%] px-2.5 py-1.5 text-sm ${mine ? "bg-indigo-600 text-white" : "bg-zinc-100 text-zinc-900"}`}>
                <div className={`text-[10px] font-700 ${mine ? "text-indigo-200" : "text-indigo-700"}`}>
                  {mine ? "You" : m.author_name} · <span className="font-400 opacity-80">{timeOf(m.created_at)}</span>
                  {(mine || isOwner) && (
                    <button onClick={() => remove(m.id)} data-testid={`trip-chat-del-${m.id}`} title="Delete message"
                      className="ml-1.5 opacity-0 group-hover:opacity-70 hover:!opacity-100 align-middle"><Trash2 className="w-3 h-3 inline" /></button>
                  )}
                </div>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      <div className="flex gap-1.5 p-2 border-t border-zinc-200">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message the family…" data-testid="trip-chat-input" maxLength={2000}
          className="flex-1 border border-zinc-300 px-2.5 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-500 bg-white" />
        <button onClick={send} disabled={busy || !text.trim()} data-testid="trip-chat-send" aria-label="Send message"
          className="px-3 bg-indigo-600 text-white disabled:opacity-40"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
